// Primitive UI Components
import { Button } from "@/tiptap/ui-primitive/button"
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuGroup,
  MenuGroupLabel,
  MenuButton,
  MenuButtonArrow,
} from "@/tiptap/ui-primitive/menu"
import { ComboboxList } from "@/tiptap/ui-primitive/combobox"
import { Separator } from "@/tiptap/ui-primitive/separator"

// Tiptap UI
import {
  TEXT_COLORS,
  useColorText,
} from "@/tiptap/ui/color-text-button"
import {
  HIGHLIGHT_COLORS,
  useColorHighlight,
} from "@/tiptap/ui/color-highlight-button"
import {
  getColorByValue,
  useRecentColors,
} from "@/tiptap/ui/color-text-popover"

// Icons
import { PaintBucketIcon } from "@/tiptap/icons/paint-bucket-icon"
import { ChevronRightIcon } from "@/tiptap/icons/chevron-right-icon"
import { TextColorSmallIcon } from "@/tiptap/icons/text-color-small-icon"
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

const TextColorMenuItem = ({ color }) => {
  const { addRecentColor } = useRecentColors()
  const { isActive, handleColorText, label } = useColorText({
    label: color.label,
    textColor: color.value,
    onApplied: ({ color, label }) =>
      addRecentColor({ type: "text", label, value: color }),
  })

  return (
    <MenuItem
      render={
        <Button variant="ghost" data-active-state={isActive ? "on" : "off"} />
      }
      onClick={handleColorText}>
      <span className="tiptap-button-color-text" style={{ color: color.value }}>
        <TextColorSmallIcon
          className="tiptap-button-icon"
          style={{ color: color.value, flexGrow: 1 }} />
      </span>
      <span className="tiptap-button-text">{label}</span>
    </MenuItem>
  );
}

const HighlightColorMenuItem = ({ color }) => {
  const { addRecentColor } = useRecentColors()
  const { isActive, handleColorHighlight, label } = useColorHighlight({
    label: color.label,
    highlightColor: color.value,
    mode: "node",
    onApplied: ({ color, label }) =>
      addRecentColor({ type: "highlight", label, value: color }),
  })

  return (
    <MenuItem
      render={
        <Button variant="ghost" data-active-state={isActive ? "on" : "off"} />
      }
      onClick={handleColorHighlight}>
      <span
        className="tiptap-button-highlight"
        style={{
          "--highlight-color": color.value
        }} />
      <span className="tiptap-button-text">{label}</span>
    </MenuItem>
  );
}

const RecentColorMenuItem = ({ colorObj }) => {
  const colorSet = colorObj.type === "text" ? TEXT_COLORS : HIGHLIGHT_COLORS
  const color = getColorByValue(colorObj.value, colorSet)

  const ColorComponent =
    colorObj.type === "text" ? TextColorMenuItem : HighlightColorMenuItem

  return <ColorComponent color={color} />;
}

/**
 * Reusable color menu component that provides text and highlight color options.
 * Includes recent colors, text colors, and highlight colors sections.
 */
export const ColorMenu = ({
  editor: providedEditor,
  trigger,
  label = "Color",
  placement = "right",
}) => {
  const { editor } = useTiptapEditor(providedEditor)
  const { recentColors, isInitialized } = useRecentColors()

  const hasColorActions =
    !!editor?.can().setMark("textStyle") ||
    !!editor?.can().setMark("highlight") ||
    !!editor?.can().toggleNodeBackgroundColor("yellow") ||
    false

  if (!editor || !hasColorActions) {
    return null
  }

  const defaultTrigger = (
    <MenuButton
      render={
        <MenuItem
          render={
            <Button variant="ghost">
              <PaintBucketIcon className="tiptap-button-icon" />
              <span className="tiptap-button-text">{label}</span>
              <MenuButtonArrow render={<ChevronRightIcon />} />
            </Button>
          } />
      } />
  )

  return (
    <Menu placement={placement} trigger={trigger || defaultTrigger}>
      <MenuContent>
        <ComboboxList style={{ maxHeight: "min(400px, var(--popover-available-height, 400px))", overflowY: "auto" }}>
          {/* Recent Colors */}
          {isInitialized && recentColors.length > 0 && (
            <MenuGroup>
              <MenuGroupLabel>Recent colors</MenuGroupLabel>
              {recentColors.map((colorObj) => (
                <RecentColorMenuItem key={colorObj.value} colorObj={colorObj} />
              ))}
              <Separator orientation="horizontal" />
            </MenuGroup>
          )}

          {/* Text Colors */}
          <MenuGroup>
            <MenuGroupLabel>Text color</MenuGroupLabel>
            {TEXT_COLORS.map((textColor) => (
              <TextColorMenuItem key={textColor.value} color={textColor} />
            ))}
          </MenuGroup>

          <Separator orientation="horizontal" />

          {/* Background Colors */}
          <MenuGroup>
            <MenuGroupLabel>Background color</MenuGroupLabel>
            {HIGHLIGHT_COLORS.map((highlightColor) => (
              <HighlightColorMenuItem key={highlightColor.value} color={highlightColor} />
            ))}
          </MenuGroup>
        </ComboboxList>
      </MenuContent>
    </Menu>
  );
}

export { TextColorMenuItem, HighlightColorMenuItem, RecentColorMenuItem }
